/**
 * Email Content Generator Utility
 *
 * Generates email subject and content for DPE-related communications.
 * 4 first-contact templates (DPE-triggered) + 2 transaction templates
 * (without DPE notions) + 1 follow-up template (only for "propriétaire certain").
 */

import type { Company } from '../../../../@types/Company';
import type { Owner } from '../../../../@types/Owner';
import { buildShortConsentUrl } from '../../../../utils/shortUrl';

export const FIRST_CONTACT_TEMPLATE = {
  REFERENT_ONLY: 'referent-only',
  DPE_REPORT: 'dpe-report',
  COPRO_REPORT: 'copro-report',
  BOTH_REPORTS: 'both-reports',
  TRANSACTION_V1: 'transaction-v1',
  TRANSACTION_V2: 'transaction-v2',
} as const;

export type FirstContactTemplateType = typeof FIRST_CONTACT_TEMPLATE[keyof typeof FIRST_CONTACT_TEMPLATE];

/** The 4 classic DPE-triggered first-contact templates. */
export const FIRST_CONTACT_DPE_OPTIONS: FirstContactTemplateType[] = [
  FIRST_CONTACT_TEMPLATE.REFERENT_ONLY,
  FIRST_CONTACT_TEMPLATE.DPE_REPORT,
  FIRST_CONTACT_TEMPLATE.COPRO_REPORT,
  FIRST_CONTACT_TEMPLATE.BOTH_REPORTS,
];

/** Transaction templates without DPE notions (shown under the DPE ones). */
export const FIRST_CONTACT_TRANSACTION_OPTIONS: FirstContactTemplateType[] = [
  FIRST_CONTACT_TEMPLATE.TRANSACTION_V1,
  FIRST_CONTACT_TEMPLATE.TRANSACTION_V2,
];

export const FIRST_CONTACT_TEMPLATE_OPTIONS: FirstContactTemplateType[] = [
  ...FIRST_CONTACT_DPE_OPTIONS,
  ...FIRST_CONTACT_TRANSACTION_OPTIONS,
];

export const isTransactionWithoutDpeTemplate = (
  templateType: FirstContactTemplateType,
): boolean => (
  templateType === FIRST_CONTACT_TEMPLATE.TRANSACTION_V1
  || templateType === FIRST_CONTACT_TEMPLATE.TRANSACTION_V2
);

export const FIRST_CONTACT_TEMPLATE_LABELS: Record<FirstContactTemplateType, string> = {
  [FIRST_CONTACT_TEMPLATE.REFERENT_ONLY]: 'Présentation référent',
  [FIRST_CONTACT_TEMPLATE.DPE_REPORT]: 'Référent + Rapport DPE',
  [FIRST_CONTACT_TEMPLATE.COPRO_REPORT]: 'Référent + Rapport copro',
  [FIRST_CONTACT_TEMPLATE.BOTH_REPORTS]: 'Référent + Les 2 rapports',
  [FIRST_CONTACT_TEMPLATE.TRANSACTION_V1]: 'V1',
  [FIRST_CONTACT_TEMPLATE.TRANSACTION_V2]: 'V2',
};

export const FIRST_CONTACT_TEMPLATE_DESCRIPTIONS: Record<FirstContactTemplateType, string> = {
  [FIRST_CONTACT_TEMPLATE.REFERENT_ONLY]: 'Présentation du rôle de référent mutation location et proposition d\'accompagnement.',
  [FIRST_CONTACT_TEMPLATE.DPE_REPORT]: 'Propose le rapport des DPE réalisés dans l\'immeuble depuis 2021.',
  [FIRST_CONTACT_TEMPLATE.COPRO_REPORT]: 'Propose un rapport détaillé sur la copropriété avec ventes récentes.',
  [FIRST_CONTACT_TEMPLATE.BOTH_REPORTS]: 'Propose les 2 rapports : DPE depuis 2021 et présentation copropriété.',
  [FIRST_CONTACT_TEMPLATE.TRANSACTION_V1]: 'Estimation prix de vente — focus état daté et rapport d\'immeuble (sans notion de DPE).',
  [FIRST_CONTACT_TEMPLATE.TRANSACTION_V2]: 'Estimation prix de vente — points clés (diagnostics, état daté, travaux, marché) sans notion de DPE.',
};

export const FOLLOW_UP_TEMPLATE = {
  STANDARD: 'follow-up-standard',
} as const;

export type FollowUpTemplateType = typeof FOLLOW_UP_TEMPLATE[keyof typeof FOLLOW_UP_TEMPLATE];

export const FOLLOW_UP_TEMPLATE_LABELS: Record<FollowUpTemplateType, string> = {
  [FOLLOW_UP_TEMPLATE.STANDARD]: 'Relance standard',
};

export const FOLLOW_UP_TEMPLATE_DESCRIPTIONS: Record<FollowUpTemplateType, string> = {
  [FOLLOW_UP_TEMPLATE.STANDARD]: 'Relance unique proposant les rapports DPE et copropriété (propriétaire certain uniquement).',
};

export interface EmailContent {
  subject: string;
  content: string;
}

/**
 * Get property information from an owner
 * Returns property name and address as a newline-separated string
 */
export const getOwnerPropertyInfo = (owner: Owner): string => {
  let propertyName = 'Copropriété non renseignée';
  
  if (owner.dpe?.copro?.name) {
    propertyName = owner.dpe.copro.name;
  } else if (owner.incomingCallCopro?.name) {
    propertyName = owner.incomingCallCopro.name;
  }
  
  let address = '';
  if (owner.dpe?.copro?.addresses && owner.dpe.copro.addresses.length > 0) {
    const coproAddress = owner.dpe.copro.addresses[0];
    address = `${coproAddress.address || ''}, ${coproAddress.postalCode || ''} ${coproAddress.city || ''}`.trim();
  } else if (owner.incomingCallCopro?.addresses && owner.incomingCallCopro.addresses.length > 0) {
    const coproAddress = owner.incomingCallCopro.addresses[0];
    address = `${coproAddress.address || ''}, ${coproAddress.postalCode || ''} ${coproAddress.city || ''}`.trim();
  }
  
  return `${propertyName}\n${address}`;
};

/**
 * Resolve the referent name for a given owner based on copro→referent mappings.
 * Falls back to company name if no specific referent is found.
 */
export const resolveReferentName = (
  owner: Owner,
  company: Company | null,
): string => {
  const referents = company?.brandingSettings?.referents;

  if (referents && referents.length > 0) {
    const r = referents[0];
    const name = `${r.firstname || ''} ${r.lastname || ''}`.trim();
    if (name) {
      return name;
    }
  }

  return company?.brandingSettings?.commercialName || company?.name || 'Votre syndic';
};

const SHARED_SUBJECT = "Informations suite à la réalisation d'un DPE d'un logement aux caractéristiques similaires au vôtre";

const TRANSACTION_V1_SUBJECT = "Vendre votre appartement : bien estimer le prix en tenant compte de l'état daté de votre bien";
const TRANSACTION_V2_SUBJECT = 'Vendre votre appartement : comment estimer le prix de votre bien avant une mise en vente ?';

/**
 * Build the shared opening paragraphs used by the 4 DPE-triggered first-contact templates.
 */
const buildSharedOpening = (): string =>
  `Je vous contacte en ma qualité de référent mutation location du syndic de votre immeuble et mon rôle est d'être à votre disposition pour vous informer et vous guider quant à vos obligations et les documents obligatoires à fournir en cas de vente ou de location de votre bien immobilier.

Dans ce cadre, nous avons remarqué qu'un « Diagnostic de Performance Énergétique » (DPE) vient d'être réalisé récemment pour un bien aux caractéristiques similaires au vôtre (si vous n'êtes pas à l'origine de ce DPE, merci de ne pas tenir compte de ce message).

En tant que syndic, nous collectons ces informations publiées par l'ADEME, afin de préparer et suivre, dans l'intérêt de la copropriété, le DPE collectif et le PPT (plan pluriannuel de travaux) de votre immeuble. Cette démarche vous a été présentée dans la newsletter adressée aux copropriétaires`;

/**
 * Build the template-specific body for each first-contact option.
 */
const buildTemplateBody = (
  templateType: FirstContactTemplateType,
  consentLink: string,
): string => {
  switch (templateType) {
    case FIRST_CONTACT_TEMPLATE.REFERENT_ONLY:
      return `En tant que référent mutation location, je peux vous aider à obtenir si vous avez un projet immobilier :
- L'état daté, s'agissant d'une vente
- L'extrait pertinent du règlement de copropriété s'agissant d'une location.

Je me tiens à votre disposition pour vous fournir toute précision quant à ces documents, selon que vous projetez de vendre ou de louer votre appartement : ${consentLink}`;

    case FIRST_CONTACT_TEMPLATE.DPE_REPORT:
      return `Si vous souhaitez obtenir de plus amples informations sur le dispositif de DPE collectif institué par le Code de la construction et de l'habitation, ainsi que sur les différents DPE des appartements de votre immeuble et leur impact potentiel sur la valeur de votre bien, je me tiens à votre disposition.

Je peux notamment vous transmettre le rapport des DPE réalisés dans votre immeuble depuis 2021. Ce document vous permettra de situer la performance énergétique de votre appartement par rapport aux autres lots de la copropriété et d'anticiper les évolutions réglementaires à venir.

Vous pourrez constater que les étiquettes énergétiques peuvent varier d'un appartement à l'autre. Ces différences s'expliquent notamment par l'étage, l'exposition, l'isolation ou encore la configuration du logement. Nous pourrons bien entendu revenir ensemble sur ces éléments si vous le souhaitez.

Par ailleurs, je me permets d'attirer votre attention sur le fait que tout copropriétaire vendeur ou bailleur doit obligatoirement fournir des documents que le syndic est en mesure de vous préparer : un état daté dans le cadre d'une vente, un extrait pertinent du règlement de copropriété dans le cadre d'une location.

Je reste à votre disposition pour échanger et vous apporter toute précision sur ces obligations si vous avez un projet immobilier : ${consentLink}`;

    case FIRST_CONTACT_TEMPLATE.COPRO_REPORT:
      return `Si vous souhaitez obtenir de plus amples informations, je peux également vous transmettre un rapport détaillé sur votre copropriété, intégrant les informations relatives aux derniers DPE réalisés dans la copropriété ainsi que celles relatives aux ventes intervenues au cours des quatre dernières années, tant au sein de l'immeuble que dans un rayon d'environ 150 mètres.

Ce document ne constitue pas un avis de valeur de votre appartement, dans la mesure où de nombreux critères spécifiques doivent être pris en compte pour établir une estimation précise. Il vous permettra néanmoins de mieux appréhender les tendances du marché local et le positionnement de votre bien.

Par ailleurs, je me permets d'attirer votre attention sur le fait que tout copropriétaire vendeur ou bailleur doit obligatoirement fournir des documents que le syndic est en mesure de vous préparer : un état daté dans le cadre d'une vente, un extrait pertinent du règlement de copropriété dans le cadre d'une location.

Je reste à votre disposition pour échanger et vous apporter toute précision sur ces obligations si vous avez un projet immobilier : ${consentLink}`;

    case FIRST_CONTACT_TEMPLATE.BOTH_REPORTS:
      return `Si vous souhaitez obtenir de plus amples informations et avoir une vision complète, je peux vous transmettre conjointement :

1 - le rapport des DPE réalisés dans votre immeuble depuis 2021 : Vous pourrez constater que les étiquettes énergétiques (lettres des DPE) peuvent varier d'un appartement à l'autre au sein d'un même immeuble.

2 - le rapport détaillé de présentation de la copropriété : Ce second document intègre les ventes intervenues au cours des quatre dernières années, tant dans l'immeuble que dans un rayon d'environ 150 mètres. Il ne constitue pas un avis de valeur, en revanche, il vous apportera une lecture claire des dynamiques du marché local et du positionnement de votre bien.

Ces rapports vous permettront notamment :
• de situer votre bien par rapport aux autres appartements
• d'identifier d'éventuels écarts de performance énergétique
• et de mieux comprendre son positionnement sur le marché local

Par ailleurs, tout copropriétaire doit fournir un document que le syndic est seul en mesure de préparer dans le cadre d'une vente : l'état daté.

Je reste à votre disposition pour échanger sur ces documents et vous apporter toute précision sur ces obligations si vous avez un projet immobilier : ${consentLink}`;

    case FIRST_CONTACT_TEMPLATE.TRANSACTION_V1:
      return `En tant que référent mutation-location au sein de votre syndic, je suis votre interlocuteur dédié pour toutes les questions liées à la vente ou à la location d'un lot. À ce titre, je souhaite attirer votre attention sur un point souvent méconnu lors de la mise en vente d'un appartement en copropriété.

À l'occasion de toute vente, le notaire demande au syndic un document appelé « état daté ». Ce document, remis à l'acquéreur avant la signature de l'acte définitif, récapitule notamment : les charges courantes et le budget de la copropriété, les sommes restant dues au syndicat, les travaux votés et à venir — dont ceux inscrits au plan pluriannuel de travaux (PPPT) — ainsi que les éventuelles procédures en cours.

Si l'acquéreur découvre à ce stade des travaux votés ou un niveau de charges qu'il n'avait pas anticipés, la vente peut s'en trouver fragilisée : renégociation du prix convenu, retard, voire remise en cause de la transaction.

C'est pourquoi une estimation réalisée sans connaissance précise de la copropriété (PPPT, travaux votés, budget, procédures) peut se révéler éloignée du prix réellement réalisable.

En tant que « Référent Mutation Location », mon rôle est de vous informer au plus près de votre copropriété en vous envoyant un rapport sur votre immeuble. Vous y trouverez notamment les premiers éléments concernant le marché immobilier local, les ventes réalisées dans un rayon de 150 mètres autour de l'immeuble au cours des quatre dernières années (extrait du fichier impots.gouv) et d'autres informations utiles.

À la suite de cette réception, je pourrai, si vous le souhaitez, échanger avec vous pour vous commenter ce document et répondre à vos questions si vous avez un projet de vente.

Si vous souhaitez ensuite aller plus loin — ou si vous disposez déjà d'une estimation et souhaitez en obtenir une seconde, complémentaire, intégrant cette fois les informations propres à votre immeuble et à votre bien — je pourrai, après notre échange, si vous le souhaitez, convenir avec vous de la visite d'une personne spécialisée du cabinet, qui se mettra en relation avec votre gestionnaire pour réaliser cette estimation en tenant compte de la vie de votre immeuble.

Cette démarche est entièrement facultative et sans engagement : vos coordonnées ne seront communiquées à la personne en charge de l'estimation que si vous en faites vous-même la demande.

Pour toute question, ou pour recevoir le rapport de votre immeuble, vous pouvez indiquer vos préférences via le lien suivant : ${consentLink}`;

    case FIRST_CONTACT_TEMPLATE.TRANSACTION_V2:
      return `En tant que référent mutation-location au sein de votre syndic, je suis votre interlocuteur dédié pour toutes les questions liées à la vente ou à la location d'un lot. À ce titre, je souhaite attirer votre attention sur les points souvent méconnus lors de la mise en vente d'un appartement en copropriété.

1 / Les diagnostics : ils sont obligatoires pour la mise en vente. S'ils sont purement informatifs, ils peuvent devenir des points bloquants pour votre acquéreur. Il est donc impératif de bien les interpréter pour estimer un bien.

2 / L'état daté : à l'occasion de toute vente, le notaire demande au syndic un document appelé « état daté ». Ce document, remis à l'acquéreur avant la signature de l'acte définitif, récapitule notamment : les charges courantes et le budget de la copropriété, les sommes restant dues au syndicat, les travaux votés et à venir — dont ceux inscrits au plan pluriannuel de travaux (PPPT) — ainsi que les éventuelles procédures en cours.

C'est pourquoi une estimation réalisée sans connaissance précise de la copropriété (PPPT, travaux votés, budget, procédures) peut se révéler éloignée du prix réellement réalisable.

3 / Les travaux de copropriété : si l'acquéreur découvre à ce stade des travaux votés ou un niveau de charges qu'il n'avait pas anticipés, la vente peut s'en trouver fragilisée : renégociation du prix convenu, retard, voire remise en cause de la transaction.

4 / Les prix de vente dans votre immeuble et alentours : dans un marché en évolution, il est impératif de bien maîtriser les valeurs de ventes réalisées dans un rayon proche. Le prix de mise en vente doit se situer dans la valeur marché pour éviter une perte de l'intérêt commercial de votre bien.

Pour vous permettre d'y voir plus clair, je peux, dans un premier temps et sur simple demande, vous adresser le rapport de copropriété de votre immeuble. Vous y trouverez déjà des éléments intéressants sur le marché immobilier : les ventes réalisées dans un rayon de 150 mètres autour de l'immeuble au cours des quatre dernières années.

À la suite de cette réception, je pourrai, si vous le souhaitez, échanger avec vous pour vous commenter ce document et répondre à vos questions si vous avez un projet de vente.

Si vous disposez déjà d'une estimation mais souhaitez en obtenir une seconde, complémentaire, intégrant cette fois les informations propres à votre immeuble — je pourrai, après notre échange, si vous le souhaitez, convenir avec vous de la visite d'une personne spécialisée du cabinet, qui se mettra en relation avec votre gestionnaire pour réaliser cette estimation en tenant compte de la vie de votre immeuble.

Cette démarche est entièrement facultative et sans engagement : vos coordonnées ne seront communiquées à la personne en charge de l'estimation que si vous en faites vous-même la demande.

Pour toute question, ou pour recevoir le rapport de votre immeuble, vous pouvez indiquer vos préférences via le lien suivant : ${consentLink}`;
  }
};

/**
 * Generate email content for DPE / transaction first-contact communication.
 * All templates are identical regardless of owner type (certain or potential).
 */
export const generateDpeEmailContent = async(
  owner: Owner,
  company: Company | null,
  privacyPolicyLink?: string | null,
  templateType: FirstContactTemplateType = FIRST_CONTACT_TEMPLATE.REFERENT_ONLY,
  jwt: string | null = null,
): Promise<EmailContent> => {
  const propertyInfo = getOwnerPropertyInfo(owner);
  const addressParts = propertyInfo.split('\n');
  const addressLine = addressParts[1] || addressParts[0] || '';

  const consentLink = await buildShortConsentUrl(company, owner.uuid, jwt);
  const companyName = company?.brandingSettings?.commercialName || company?.name || 'Votre syndic';
  const referentName = resolveReferentName(owner, company);

  const privacyPolicyLine = privacyPolicyLink
    ? `\nLien vers notre politique de traitement : ${privacyPolicyLink}`
    : '';

  const isTransaction = isTransactionWithoutDpeTemplate(templateType);
  const body = buildTemplateBody(templateType, consentLink);

  const subjectBase = templateType === FIRST_CONTACT_TEMPLATE.TRANSACTION_V1
    ? TRANSACTION_V1_SUBJECT
    : templateType === FIRST_CONTACT_TEMPLATE.TRANSACTION_V2
      ? TRANSACTION_V2_SUBJECT
      : SHARED_SUBJECT;

  const greeting = isTransaction ? 'Madame, Monsieur,' : 'Cher Copropriétaire,';
  const closingLine = isTransaction
    ? 'Je reste à votre disposition,'
    : templateType === FIRST_CONTACT_TEMPLATE.BOTH_REPORTS
      ? 'Cordialement,'
      : 'Veuillez agréer, Cher Copropriétaire, l\'expression de nos salutations distinguées.';

  const contentBody = isTransaction
    ? body
    : `${buildSharedOpening()}\n\n${body}`;

  return {
    subject: `${subjectBase} ${addressLine}`.trim(),
    content: `${greeting}

${contentBody}

${closingLine}

${referentName}
Référent Mutation Location
${companyName}

Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, d'effacement et de portabilité de vos données. Vous pouvez exercer ces droits en nous contactant.${privacyPolicyLine}`,
  };
};

/**
 * Generate the "consent confirmation after phone call" email.
 * Sent by the référent mutation location after a successful call where the
 * owner agreed to be put in contact with a transactionnaire or gestionnaire
 * locatif. The content is legally validated and must not be altered.
 */
export const generateConsentAfterCallEmailContent = (
  owner: Owner,
  company: Company | null,
  collaboratorName: string,
  serviceType: 'transaction' | 'gestion-locative',
  callDate: Date,
  privacyPolicyLink?: string | null,
): EmailContent => {
  const civility = owner.civility === 2 ? 'Madame' : 'Monsieur';
  const ownerLastname = owner.lastname || '';
  const referentName = resolveReferentName(owner, company);

  const serviceLabel = serviceType === 'transaction'
    ? 'la transaction'
    : 'la gestion locative';

  const formattedDate = callDate.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const privacyPolicyLine = privacyPolicyLink
    ? `\nLien politique de traitement du cabinet : ${privacyPolicyLink}`
    : '';

  return {
    subject: `Transmission de vos coordonnées à ${collaboratorName} comme convenu`,
    content: `Bonjour ${civility} ${ownerLastname},

Comme convenu lors de notre échange téléphonique du ${formattedDate}, j'ai transmis vos coordonnées à ${collaboratorName}, en charge de ${serviceLabel}, qui prendra contact avec vous dans les prochains jours afin de convenir d'un rendez-vous.

Je reste naturellement à votre disposition pour toute question relative à la copropriété, aux démarches à anticiper ou aux documents nécessaires dans le cadre de votre projet.

Dans le cadre de cette mise en relation, vos données sont transmises exclusivement au collaborateur concerné, sur la base de votre accord. Vous disposez d'un droit d'accès, de rectification et d'opposition que vous pouvez exercer à tout moment.

Si vous souhaitez revenir sur cette demande ou en modifier les modalités, vous pouvez bien entendu me contacter.

Je vous prie d'agréer, ${civility}, l'expression de mes salutations distinguées.

${referentName}
Référent Mutation Location${privacyPolicyLine}`,
  };
};

/**
 * Generate follow-up email content.
 * Single template, only for "propriétaire certain" (enforced by caller).
 */
export const generateFollowUpEmailContent = async(
  owner: Owner,
  company: Company | null,
  privacyPolicyLink?: string | null,
  _templateType?: FollowUpTemplateType,
  jwt: string | null = null,
): Promise<EmailContent> => {
  const propertyInfo = getOwnerPropertyInfo(owner);
  const addressParts = propertyInfo.split('\n');
  const addressLine = addressParts[1] || addressParts[0] || '';

  const consentLink = await buildShortConsentUrl(company, owner.uuid, jwt);
  const companyName = company?.brandingSettings?.commercialName || company?.name || 'Votre syndic';
  const referentName = resolveReferentName(owner, company);

  const privacyPolicyLine = privacyPolicyLink
    ? `\nLien vers notre politique de traitement : ${privacyPolicyLink}`
    : '';

  return {
    subject: `${SHARED_SUBJECT} ${addressLine}`,
    content: `Cher Copropriétaire,

N'ayant pas eu de réponse suite à mon dernier mail, je me permets de revenir vers vous concernant le DPE réalisé dans votre immeuble.

Je vous contacte en ma qualité de référent mutation location du syndic de votre immeuble et mon rôle est d'être à votre disposition pour vous informer et vous guider quant à vos obligations et les documents obligatoires à fournir en cas de vente ou de location de votre bien immobilier.

Si vous êtes à l'origine de ce DPE, je vous renouvelle ma proposition si vous le souhaitez, vous préparer gratuitement :
• les DPE de votre immeuble depuis 2021
• ainsi qu'un rapport sur les ventes récentes dans votre copropriété et à proximité

Ces éléments vous permettront d'avoir une vue plus précise de la situation de votre bien au regard de :
- La performance énergétique comparative de votre appartement
- Et de son positionnement actuel sur le marché

Par ailleurs, tout copropriétaire vendeur doit fournir des documents que le syndic est seul en mesure de préparer (état daté). Je reste à votre disposition pour vous accompagner.

Je reste à votre disposition pour vous accompagner dans ces démarches et nous pouvons si vous le désirez échanger simplement lors d'un rendez-vous téléphonique ou en visioconférence (15 à 30 min) : ${consentLink}

Restant à votre disposition,

Cordialement,

${referentName}
Référent Mutation Location
${companyName}

Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, d'effacement et de portabilité de vos données. Vous pouvez exercer ces droits en nous contactant.${privacyPolicyLine}`,
  };
};
